import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "./AuthContext";

const FleetAssistantContext = createContext(null);
const STORAGE_KEY_BASE = "fleetAssistant:isOpen:v1";

const parseStoredOpenState = (value) => String(value || "").toLowerCase() === "1" || String(value || "").toLowerCase() === "true";

export function FleetAssistantProvider({ children }) {
  const { getFleetID } = useContext(AuthContext);
  const [isAssistantOpen, setAssistantOpen] = useState(false);
  const [isStorageHydrated, setStorageHydrated] = useState(false);

  const fleetId = String(getFleetID?.() || "").trim();
  const storageKey = fleetId ? `${STORAGE_KEY_BASE}:${fleetId}` : STORAGE_KEY_BASE;

  useEffect(() => {
    let nextValue = false;
    try {
      nextValue = parseStoredOpenState(window.localStorage.getItem(storageKey));
    } catch (error) {
      console.error("FleetAssistant localStorage read failed:", error);
    }
    setAssistantOpen(nextValue);
    setStorageHydrated(true);
  }, [storageKey]);

  useEffect(() => {
    if (!isStorageHydrated) return;
    try {
      window.localStorage.setItem(storageKey, isAssistantOpen ? "1" : "0");
    } catch (error) {
      console.error("FleetAssistant localStorage write failed:", error);
    }
  }, [isAssistantOpen, isStorageHydrated, storageKey]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.storageArea !== window.localStorage) return;
      if (event.key !== storageKey) return;
      const nextValue = parseStoredOpenState(event.newValue);
      setAssistantOpen((previousValue) => (previousValue === nextValue ? previousValue : nextValue));
    };

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, [storageKey]);

  const openAssistant = useCallback(() => setAssistantOpen(true), []);
  const closeAssistant = useCallback(() => setAssistantOpen(false), []);
  const toggleAssistant = useCallback(() => setAssistantOpen((prev) => !prev), []);

  const value = useMemo(
    () => ({
      isAssistantOpen,
      openAssistant,
      closeAssistant,
      toggleAssistant,
    }),
    [closeAssistant, isAssistantOpen, openAssistant, toggleAssistant]
  );

  return (
    <FleetAssistantContext.Provider value={value}>{children}</FleetAssistantContext.Provider>
  );
}

export function useFleetAssistant() {
  const context = useContext(FleetAssistantContext);
  if (!context) {
    throw new Error("useFleetAssistant must be used within FleetAssistantProvider");
  }
  return context;
}
